import React, { useCallback, useEffect, useState } from 'react';
import { marketDataApi } from '../api/marketDataApi';

const REFRESH_MS = 30_000;

function formatPrice(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  return n >= 100 ? n.toFixed(2) : n.toFixed(2);
}

function formatChg(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}`;
}

function formatPct(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}%`;
}

/**
 * Sticky tape of major index ETFs: last price + day change vs prior close.
 */
const MarketIndexTape = () => {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await marketDataApi.getIndices();
      const data = res?.data ?? res ?? [];
      setRows(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load indices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  if (!loading && !rows.length && !error) return null;

  return (
    <div className="border-b border-hairline bg-elevated/80 backdrop-blur-sm">
      <div className="mx-auto max-w-[1600px] px-4 lg:px-6">
        <div className="flex items-center gap-1 overflow-x-auto py-1.5 scrollbar-thin">
          <span className="lt-label whitespace-nowrap pr-2 text-[9px] tracking-[0.14em] text-muted flex-shrink-0">
            MARKETS
          </span>
          {loading && !rows.length && (
            <span className="text-[11px] text-muted py-1">Loading indices…</span>
          )}
          {error && !rows.length && (
            <span className="text-[11px] text-bear py-1">{error}</span>
          )}
          {rows.map((row) => {
            const up = (row.changePct ?? 0) > 0;
            const down = (row.changePct ?? 0) < 0;
            const tone = up ? 'text-bull' : down ? 'text-bear' : 'text-muted';
            const chip = up
              ? 'bg-[var(--lt-bull-dim)]'
              : down
                ? 'bg-[var(--lt-bear-dim)]'
                : 'bg-canvas';
            return (
              <div
                key={row.symbol}
                className="flex items-baseline gap-1.5 flex-shrink-0 px-2.5 py-1 rounded-md border border-hairline bg-surface/60"
                title={`${row.label} (${row.symbol}) · vs prior close`}
              >
                <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">
                  {row.short || row.symbol}
                </span>
                <span className="text-[12px] font-semibold tabular-nums text-ink">
                  {formatPrice(row.price)}
                </span>
                <span
                  className={`text-[10px] font-semibold tabular-nums px-1 py-0.5 rounded ${chip} ${tone}`}
                >
                  {formatPct(row.changePct)}
                </span>
                <span className={`hidden sm:inline text-[10px] tabular-nums ${tone}`}>
                  {formatChg(row.change)}
                </span>
              </div>
            );
          })}
          {error && rows.length > 0 && (
            <span className="text-[10px] text-bear ml-2 flex-shrink-0">{error}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketIndexTape;
